import { useEffect, useMemo } from 'react';
import _ from 'lodash';
import { Link, Typography } from '@mui/material';
import { DataGrid, GridColumnHeaderSortIcon } from '@mui/x-data-grid';

import { useSelector } from 'BMapsRedux';
import { JobTypes, lookupJobDefinition } from 'BMapsSrc/jobman';
import DialogContentSection from 'BMapsUI/common/DialogContentSection';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import { GridToolBar } from 'BMapsUI/tables/CustomToolbar';
import {
    ScreeningCoralMap, sortScreeningProperties,
} from 'BMapsUI/tables/graphs/CoralComponents';
import { hexToHsl } from 'BMapsSrc/util/color_utils';
import { applyColumnDirectionality, getFormattedValue } from 'BMapsUI/tables/BMapsTable';
import { CustomColumnsPanel } from 'BMapsUI/tables/CustomColumnsPanel';
import { setActiveChart, setScreeningTableState } from 'BMapsRedux/projectState/access';
import { showAlert } from 'BMapsSrc/utils';
import { dataInfoTooltip } from '../../ImportPane/ImportCompoundPane/Tabs/JobsResultsTab';
import { openScreeningSidePanel } from '../ScreeningSidePanel';

export default function ScreeningResults({ selected, activeChart }) {
    const { localJobState, displayedScreeningJobs } = useSelector((state) => state.jobs);

    const results = useMemo(
        () => displayedScreeningJobs.flatMap((job) => localJobState[job.jobid]?.results || []),
        [localJobState, displayedScreeningJobs]
    );

    if (!selected) return null;

    let message; // content when no table to display
    if (!displayedScreeningJobs?.length) {
        message = (
            <span>
                No screening results to display.
                <br />
                Add results from the
                {' '}
                <Link /* eslint-disable-line jsx-a11y/anchor-is-valid */
                    component="button"
                    onClick={() => openScreeningSidePanel('screen_jobs_tab')}
                    sx={{ color: 'var(--marketing-blue)' }}
                >
                    Screening Jobs Tab
                </Link>
                {' '}
                to view them here.
            </span>
        );
    } else if (!results?.length) {
        message = (
            <span>
                {`The selected screening ${displayedScreeningJobs.length > 1 ? 'jobs' : 'job'} `}
                produced no results.
            </span>
        );
    }
    return (
        <DialogContentSection
            {...scoringTitleProps(displayedScreeningJobs)}
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
            disablePaddingTop
        >
            <Typography
                component="div"
                sx={{
                    display: 'flex', flexDirection: 'column', height: '100%',
                }}
            >
                {message || <ScreeningResultsTable results={results} activeChart={activeChart} />}
            </Typography>

        </DialogContentSection>
    );
}

function scoringTitleProps(displayedScreeningJobs) {
    function screeningJobName(job, quote) {
        const name = job.requestmeta?.jobName || `Screen started ${new Date(job.created).toLocaleString('en-US', {
            dateStyle: 'short',
            timeStyle: 'short',
        })}`;
        return quote ? `"${name}"` : name;
    }

    const title = 'Screening Results';
    if (!displayedScreeningJobs || displayedScreeningJobs.length <= 0) {
        return { title };
    }

    let resultName = screeningJobName(displayedScreeningJobs[0], true);
    if (displayedScreeningJobs.length > 1) {
        resultName += ` and ${displayedScreeningJobs.length - 1} more`;
    }
    const activeJobsDesc = (
        <BMapsTooltip
            title={(
                <div>
                    Loaded Screening Results:
                    <ul style={{ paddingLeft: '1.2em', margin: 0 }}>
                        {displayedScreeningJobs.map((job, i) => (
                            <li key={`${i.toString()}`}>{screeningJobName(job)}</li>
                        ))}
                    </ul>
                </div>
            )}
        >
            {resultName}
        </BMapsTooltip>
    );

    return {
        title: (
            <div>
                {title.toUpperCase()}
                {' '}
                &ndash;
                {' '}
                {activeJobsDesc}
            </div>
        ),
        titleUpperCase: false,
    };
}

function ScreeningResultsTable({
    results, activeChart,
}) {
    const tableState = useSelector((state) => state.projectState.tableState.screeningTable);
    const columnWidths = tableState?.columnWidths || {};

    const setColumnWidths = (widths) => {
        setScreeningTableState({
            columnWidths: widths,
        });
    };

    const screeningCompoundInfoList = tableState?.screeningCompoundInfoList
        ?.map((c) => new ScreeningCompoundInfo(c)) || [];

    /**
     * @param {ScreeningCompoundInfo[]} newScreeningCompoundInfo
     */
    const updateScreeningCompoundInfoState = (newScreeningCompoundInfo) => {
        const screeningCompoundInfoState = newScreeningCompoundInfo
            .map((c) => c.getSavedState());
        setScreeningTableState({
            screeningCompoundInfoList: screeningCompoundInfoState,
        });

        // update the column visibility model
        const columnVisibilityModel = tableState?.columnVisibilityModel;
        if (columnVisibilityModel) {
            const cmpdNames = newScreeningCompoundInfo
                .filter((c) => c.active)
                .map((c) => getNumberedCompoundName(c, newScreeningCompoundInfo));
            // Get the current property visibility from the column visibility model,
            // which has columns named as "<property> <compound>"
            const perPropVisibility = {};
            for (const [key, value] of Object.entries(columnVisibilityModel)) {
                const prop = key.split(' ')[0];
                if (perPropVisibility[prop] == null) {
                    perPropVisibility[prop] = value;
                }
            }
            // Now build a new visibility model for columns for all properties
            // against the active compounds.
            const newVisibilityModel = {};
            for (const [key, value] of Object.entries(perPropVisibility)) {
                for (const cmpdName of cmpdNames) {
                    newVisibilityModel[`${key} ${cmpdName}`] = value;
                }
            }
            setScreeningTableState({
                columnVisibilityModel: newVisibilityModel,
            });
        }
    };

    useEffect(() => {
        if (!results || results.length === 0) return;
        const colors = ['#ff0000', '#377eb8', '#4daf4a'];
        const compoundMap = new Map();
        const overlappingCompounds = [];
        const newAvailableScreeningCompoundInfoList = results.map((inputData, i) => {
            let screeningCompoundInfo = null;
            const alreadyAvailableScreeningCompoundInfo = screeningCompoundInfoList.find(
                (c) => c.smiles === inputData.structure
            );
            if (alreadyAvailableScreeningCompoundInfo) {
                screeningCompoundInfo = alreadyAvailableScreeningCompoundInfo;
            } else {
                screeningCompoundInfo = new ScreeningCompoundInfo({
                    smiles: inputData.structure,
                    name: inputData.name,
                    baseColor: colors[i % colors.length],
                    active: false,
                });
            }
            if (!compoundMap.has(screeningCompoundInfo.smiles)) {
                compoundMap.set(screeningCompoundInfo.smiles, inputData.properties);
            } else {
                if (!overlappingCompounds.includes(screeningCompoundInfo.smiles)) {
                    overlappingCompounds.push(screeningCompoundInfo.smiles);
                }
            }
            return screeningCompoundInfo;
        });
        if (overlappingCompounds.length > 0) {
            showAlert(
                'Duplicate Screening Compounds',
                [
                    'You have loaded screening jobs with overlapping compounds.',
                    'Compounds associated with the following SMILES will show screening properties for only one target structure:',
                    ...overlappingCompounds.map((c) => `* ${c}`),
                ]
            );
        }
        const uniqueScreeningCompoundInfo = _.uniqBy(newAvailableScreeningCompoundInfoList, 'smiles');
        // Initial active compound is the first unique compound
        if (uniqueScreeningCompoundInfo.filter((c) => c.active).length === 0
            && uniqueScreeningCompoundInfo.length > 0) {
            uniqueScreeningCompoundInfo[0].active = true;
        }
        for (const screeningCompoundInfo of uniqueScreeningCompoundInfo) {
            screeningCompoundInfo.properties = compoundMap.get(screeningCompoundInfo.smiles);
        }
        updateScreeningCompoundInfoState(uniqueScreeningCompoundInfo);
    }, [results]);

    if (!screeningCompoundInfoList || screeningCompoundInfoList.length === 0) return null;

    const availableCharts = [{
        id: 'bmapsTable',
        name: 'Table',
        componentProps: {
            rows: [],
            columns: [],
        },

    },
    {
        id: 'coralMap',
        name: 'Coral Map',
        componentProps: {},
    }];

    const {
        rows, columns, columnGroupingModel, defaultColumnVisibility,
    } = buildScreeningResultsTable(screeningCompoundInfoList, columnWidths);

    function handleColumnWidthChange(params) {
        const newWidths = { ...columnWidths, [params.colDef.field]: params.width };
        setColumnWidths(newWidths);
    }
    return (
        <>
            { activeChart === 'bmapsTable'
                && (
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        columnGroupingModel={columnGroupingModel}
                        pageSize={5}
                        rowsPerPageOptions={[5]}
                        rowHeight={60}
                        columnVisibilityModel={
                            tableState?.columnVisibilityModel
                            || defaultColumnVisibility
                        }
                        onColumnVisibilityModelChange={(columnVisibilityModel) => {
                            setScreeningTableState({ columnVisibilityModel });
                        }}
                        onColumnWidthChange={handleColumnWidthChange}
                        showToolbar
                        slotProps={{
                            basePopper: { placement: 'bottom-start' },
                        }}
                        slots={{
                            toolbar: (toolbarProps) => (
                                <GridToolBar
                                    {...toolbarProps}
                                    toolBarId="ScreeningResultsTable"
                                    charts={availableCharts}
                                    setChart={(chart) => setActiveChart(chart, 'screeningTable')}
                                    availableScreeningCompoundInfoList={screeningCompoundInfoList}
                                    updateScreeningCompoundInfo={updateScreeningCompoundInfoState}
                                    useEditCompoundsButton
                                />
                            ),
                            columnsPanel: CustomColumnsPanel,
                            columnHeaderSortIcon: (props) => {
                                const found = columns.find((c) => c.field === props.field);
                                const title = found ? `Sort by ${found.headerName}` : 'Sort';
                                return (
                                    <BMapsTooltip title={title} noMaxWidth>
                                        <GridColumnHeaderSortIcon {...props} />
                                    </BMapsTooltip>
                                );
                            },
                        }}
                        sx={{
                            '& .compound-header': {
                                backgroundColor: 'lightGray',
                                borderRight: '1px solid white',
                                borderLeft: '1px solid white',
                            },
                            '& .MuiDataGrid-columnHeader--emptyGroup': {
                                borderBottom: '1px solid lightgray',
                                '& .MuiDataGrid-columnSeparator': {
                                    display: 'none',
                                },
                            },
                            '& .MuiTablePagination-root': {
                                overflow: 'hidden',
                            },
                            '& .MuiDataGrid-footerContainer': {
                                minHeight: '3rem',
                            },
                        }}
                    />
                )}
            { activeChart === 'coralMap' && (
            <ScreeningCoralMap
                activeScreeningCompoundInfoList={
                                        screeningCompoundInfoList.filter((c) => c.active)
                                    }
                availableCharts={availableCharts}
                setChart={(chart) => setActiveChart(chart, 'screeningTable')}
                availableScreeningCompoundInfoList={screeningCompoundInfoList}
                updateScreeningCompoundInfo={updateScreeningCompoundInfoState}
            />
            )}
        </>
    );
}
function makeColumnKey(prop, compound, allCompounds) {
    const headerName = getNumberedCompoundName(compound, allCompounds);
    if (!compound) {
        return prop;
    }
    return `${prop} ${headerName}`;
}

/**
 * Unnamed compounds are named "Compound X" where X is their position in the list of compounds.
 * Named compounds that appear multiple times are named "Name (Y)" where Y is their position
 * among compounds with the same name.
 * @param {ScreeningCompoundInfo} screeningCompoundInfo
 * @param {ScreeningCompoundInfo[]} screeningCompoundInfoList
 * @returns {string}
 */
export function getNumberedCompoundName(screeningCompoundInfo, screeningCompoundInfoList) {
    const defaultName = 'Compound';
    const name = screeningCompoundInfo.name;
    if (!name || name.trim() === '') {
        // For unnamed compounds, use position in list of available compounds
        const index = screeningCompoundInfoList.indexOf(screeningCompoundInfo);
        return `${defaultName} ${index + 1}`;
    }

    const sameCompounds = screeningCompoundInfoList.filter((c) => c.name === name);
    if (sameCompounds.length > 1) {
        // Append position among compounds with the same name
        const compoundIndex = sameCompounds.indexOf(screeningCompoundInfo);
        return `${name} (${compoundIndex + 1})`;
    }
    return name;
}

function buildScreeningResultsTable(screeningCompoundInfoList, columnWidths) {
    const activeScreeningCompoundInfoList = screeningCompoundInfoList
        .filter((c) => c.active);

    if (!activeScreeningCompoundInfoList || activeScreeningCompoundInfoList.length === 0) {
        return {
            rows: [],
            columns: [],
            columnGroupingModel: [],
            defaultColumnVisibility: {},
        };
    }
    const columns = [{
        field: 'target',
        headerName: 'Target',
        title: 'Target',
        width: columnWidths.target,
        flex: columnWidths.target ? undefined : 1,
    }];
    const rows = [];
    const propertyList = [];
    const propInfos = lookupJobDefinition(JobTypes.LIBRARY_SCREEN).dataInfo;
    const defaultColumnVisibility = {};

    for (const activeScreeningCompoundInfo of activeScreeningCompoundInfoList) {
        const properties = activeScreeningCompoundInfo.properties;
        const cmpdLabel = getNumberedCompoundName(
            activeScreeningCompoundInfo, screeningCompoundInfoList
        );
        for (const [targetId, propsAgainstTarget] of Object.entries(properties)) {
            const propValues = {};
            const propsAgainstTargetList = Object.keys(propsAgainstTarget).map((p) => ({
                property: p,
                propertyValue: propsAgainstTarget[p],
            }));

            // Sort columns to match the order in propInfos
            sortScreeningProperties(propsAgainstTargetList, (c) => c.property);

            for (const { property, propertyValue } of propsAgainstTargetList) {
                const propInfo = propInfos[property] || {};
                const propertyColKey = makeColumnKey(
                    property, activeScreeningCompoundInfo, screeningCompoundInfoList
                );
                const propLabel = propInfo.label || property;
                const labelWithCmpd = `${propLabel} ${cmpdLabel}`;
                if (!columns.find((column) => column?.field === propertyColKey)) {
                    const columnInfo = {
                        field: propertyColKey,
                        headerName: labelWithCmpd,
                        title: propLabel,
                        description: dataInfoTooltip(propInfos[property]),
                        width: columnWidths[propertyColKey],
                        flex: columnWidths[propertyColKey] ? undefined : 1,
                        type: propInfo.type || 'number',
                        renderHeader: () => (
                            <BMapsTooltip title={`${propLabel} for ${cmpdLabel}`} noMaxWidth>
                                <span>{propLabel}</span>
                            </BMapsTooltip>
                        ),
                        renderCell: ({ value: propValue }) => (
                            <BMapsTooltip title={propValue} placement="bottom-start">
                                <span>{getFormattedValue(propValue, propInfo.precision)}</span>
                            </BMapsTooltip>
                        ),
                    };
                    applyColumnDirectionality(columnInfo, propInfo.directionality);
                    columns.push(columnInfo);
                }
                propValues[propertyColKey] = propertyValue;
                if (!propertyList.includes(property)) propertyList.push(property);
                if (!propInfo.defaultVisible) defaultColumnVisibility[propertyColKey] = false;
            }
            const existingRowIndex = rows.findIndex((row) => row.target === targetId);
            if (existingRowIndex !== -1) {
                rows[existingRowIndex] = {
                    ...rows[existingRowIndex],
                    ...propValues,
                };
            } else {
                rows.push({
                    id: targetId,
                    target: targetId,
                    ...propValues,
                });
            }
        }
    }

    const columnGroupingModel = [
        ...activeScreeningCompoundInfoList.map((compound) => {
            const headerName = getNumberedCompoundName(compound, screeningCompoundInfoList);
            return {
                groupId: headerName,
                headerName,
                headerAlign: 'center',
                headerClassName: 'compound-header',
                children: propertyList.map(
                    (key) => ({ field: makeColumnKey(key, compound, screeningCompoundInfoList) })
                ),
            };
        }),
    ];
    return {
        rows, columns, columnGroupingModel, defaultColumnVisibility,
    };
}

/**
 * @typedef {{
 * [kinaseOrGroup: string]: {
 * branchColor: string, nodeSize: number, tooltip: JSX.Element
 * },
 * branchColor: string, label: {textColor: string}, nodeSize: number, tooltip: JSX.Element
 * }} CoralStyle
 */

/**
 * @typedef {{
 * structure: string,
 * properties: {
 * [kinase: string]: {
 * [property: string]: number
 * }
 * }
 * }[]} inputData
 */

/**
 * @typedef {{ smiles: string, baseHue: number }} coralCompound
 */

export class ScreeningCompoundInfo {
    constructor({
        smiles, name='', properties={}, baseColor='#ff0000', active=false,
    }) {
        /** @type {string} */
        this.smiles = smiles;
        /** @type {string} */
        this.baseColor = baseColor;
        /** @type {{ [kinase: string]: { [property: string]: number } }} */
        this.properties = properties;
        this.name = name;
        this.active = active;
    }

    /**
     * @param {string} property
     * @returns {{ [kinase: string]: number }}
     */
    getKinaseValuesForProperty(property) {
        const kinaseValues = {};
        for (const [kinase, properties] of Object.entries(this.properties)) {
            kinaseValues[kinase] = properties[property];
        }
        return kinaseValues;
    }

    /**
     * @param {string} kinase
     */
    getPropertiesForKinase(kinase) {
        return this.properties[kinase];
    }

    getPropertyValue(kinase, property) {
        return this.properties[kinase][property];
    }

    setProperties(properties) {
        this.properties = properties;
    }

    getPropertyList() {
        const retProperties = [];
        for (const [kinase, properties] of Object.entries(this.properties)) {
            for (const [property, value] of Object.entries(properties)) {
                if (!retProperties.includes(property)) retProperties.push(property);
            }
        }
        return retProperties;
    }

    // getPropertyList() {
    //     const propInfos = lookupJobDefinition(JobTypes.LIBRARY_SCREEN).dataInfo;
    //     const retProperties = [];
    //     for (const [kinase, properties] of Object.entries(this.properties)) {
    //         for (const [key, value] of Object.entries(properties)) {
    //             const propInfo = propInfos[key] || {};
    //             if (!retProperties.find((p) => p.key === key)) {
    //                 retProperties.push({
    //                     key,
    //                     label: propInfo.label || key,
    //                     description: propInfo.description || '',
    //                     unit: propInfo.unit || '',
    //                     directionality: propInfo.directionality || null,
    //                 });
    //             }
    //         }
    //     }
    //     return retProperties;
    // }

    getMaxValue(property, maxThreshold) {
        let maxValue = null;
        for (const [kinase, properties] of Object.entries(this.properties)) {
            const propertyValue = properties[property];
            if (propertyValue == null) continue;
            if (maxThreshold != null && propertyValue > maxThreshold) continue;
            if (maxValue == null || maxValue < propertyValue) maxValue = propertyValue;
        }
        return maxValue;
    }

    getMinValue(property, minThreshold) {
        let minValue = null;
        for (const [kinase, properties] of Object.entries(this.properties)) {
            const propertyValue = properties[property];
            if (propertyValue == null) continue;
            if (minThreshold != null && propertyValue < minThreshold) continue;
            if (minValue == null || minValue > propertyValue) minValue = propertyValue;
        }
        return minValue;
    }

    getBaseHue() {
        const hexNum = parseInt(this.baseColor.replace(/^#/, ''), 16);
        return hexToHsl(hexNum)[0];
    }

    getSavedState() {
        return {
            smiles: this.smiles,
            baseColor: this.baseColor,
            name: this.name,
            active: this.active,
            properties: this.properties,
        };
    }
}
